import React, { useEffect, useState } from "react";
import { Typography, IconButton } from "@mhome/ui";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import SkipPreviousIcon from "@mui/icons-material/SkipPrevious";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import { SyncBadge } from "../../common/badges";
import { useComponentBackgroundColor } from "../../common/adaptive-theme-provider";

const MediaPlaybackDetail1Widget = ({ widget, onValueUpdate, pattern }) => {
  const getComponentBgColor = useComponentBackgroundColor();
  const [v0, setV0] = useState({});
  const [playStatus, setPlayStatus] = useState("stopped");
  const [w1, setW1] = useState(null); // play status widget
  const [w2, setW2] = useState(null); // track command widget

  useEffect(() => {
    setV0(widget);

    // Handle play status from $w1 (if present)
    if (widget?.items?.$w1) {
      setW1(widget.items.$w1);
      const status = widget.items.$w1.value;
      if (["playing", "paused", "stopped"].includes(status)) {
        setPlayStatus(status);
      } else {
        setPlayStatus("stopped");
      }
    } else {
      setW1(null);
      setPlayStatus("stopped");
    }

    // Handle track commands from $w2 (if present)
    if (widget?.items?.$w2) {
      setW2(widget.items.$w2);
    } else {
      setW2(null);
    }
  }, [widget]);

  const sendPlayStatusUpdate = (status) => {
    if (!w1) return; // Don't send update if play status widget doesn't exist

    const updatedWidget = {
      ...v0.items.$w1,
      value: status,
    };
    onValueUpdate(updatedWidget);
  };

  const sendTrackCommandUpdate = (command) => {
    if (!w2) return; // Don't send update if track command widget doesn't exist

    const updatedWidget = {
      ...v0.items.$w2,
      value: command,
    };
    onValueUpdate(updatedWidget);
  };

  const handlePlayPause = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!w1) return;
    if (pattern && pattern.endsWith("_R")) return;

    let newStatus;
    if (playStatus === "playing") {
      newStatus = "paused";
    } else {
      newStatus = "playing";
    }

    setPlayStatus(newStatus);
    sendPlayStatusUpdate(newStatus);
  };

  const handlePrevious = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!w2) return;
    if (pattern && pattern.endsWith("_R")) return;

    sendTrackCommandUpdate("prev");
  };

  const handleNext = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!w2) return;
    if (pattern && pattern.endsWith("_R")) return;

    sendTrackCommandUpdate("next");
  };

  // Determine which play/pause icon to show (consistent with media-playback-1.js)
  const getPlayIcon = () => {
    switch (playStatus) {
      case "playing":
        return PauseIcon;
      case "paused":
        return PlayArrowIcon;
      case "stopped":
        return PlayArrowIcon;
      default:
        return PlayArrowIcon;
    }
  };

  const PlayIcon = getPlayIcon();
  const disabled = pattern && pattern.endsWith("_R");

  return (
    <div className="w-full flex flex-col gap-4">
      {pattern && pattern.endsWith("_W") && (
        <div className="flex justify-end">
          <SyncBadge />
        </div>
      )}

      {/* Wrapped controls section (like volume-mute-detail-1.js) */}
      <div
        className="flex flex-col rounded-2xl px-3 py-2"
        style={{
          backgroundColor: getComponentBgColor("hsl(var(--card))"),
        }}
      >
        <Typography className="mb-3 font-bold">Media Controls</Typography>

        <div
          className="flex items-center justify-center gap-6 px-2 overflow-visible"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Previous button - only show if $w2 exists */}
          {w2 && (
            <IconButton
              onClick={handlePrevious}
              className="p-1 hover:bg-[rgba(25,118,210,0.04)]"
              style={{
                color: "hsl(var(--primary))",
                cursor: disabled ? "default" : "pointer",
                opacity: disabled ? 0.8 : 1,
              }}
              disabled={disabled}
            >
              <SkipPreviousIcon style={{ fontSize: "2rem" }} />
            </IconButton>
          )}

          {/* Play/Pause button - only show if $w1 exists */}
          {w1 && (
            <IconButton
              onClick={handlePlayPause}
              className={`p-1 ${
                playStatus === "playing"
                  ? "hover:bg-[rgba(25,118,210,0.12)]"
                  : "hover:bg-[rgba(25,118,210,0.04)]"
              }`}
              style={{
                color: "hsl(var(--primary))",
                cursor: disabled ? "default" : "pointer",
                opacity: disabled ? 0.8 : 1,
                backgroundColor:
                  playStatus === "playing"
                    ? "rgba(25, 118, 210, 0.08)"
                    : "transparent",
              }}
              disabled={disabled}
            >
              <PlayIcon style={{ fontSize: "2.5rem" }} />
            </IconButton>
          )}

          {/* Next button - only show if $w2 exists */}
          {w2 && (
            <IconButton
              onClick={handleNext}
              className="p-1 hover:bg-[rgba(25,118,210,0.04)]"
              style={{
                color: "hsl(var(--primary))",
                cursor: disabled ? "default" : "pointer",
                opacity: disabled ? 0.8 : 1,
              }}
              disabled={disabled}
            >
              <SkipNextIcon style={{ fontSize: "2rem" }} />
            </IconButton>
          )}
        </div>
      </div>
    </div>
  );
};

export default MediaPlaybackDetail1Widget;
